import React, { useState, useRef, useEffect } from 'react';
import { isMaster } from '@/lib/permissions';
import { api } from '@/App';
import extractErrorMessage from '@/utils/extractErrorMessage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, Trash2, Database, ShoppingCart, Receipt, CreditCard, RotateCcw, Download, Upload, CheckCircle, HardDrive, RefreshCw, FileJson, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const DatabaseManagement = ({ user }) => {
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, title: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isMaster(user)) {
      fetchBackups();
    }
  }, [user]);

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const response = await api.get('/admin/backups');
      setBackups(response.data);
    } catch (error) {
      console.error('Failed to fetch backups:', error);
    } finally {
      setBackupsLoading(false);
    }
  };

  const showConfirmDialog = (action, title, description) => {
    setConfirmDialog({
      open: true,
      action,
      title,
      description
    });
  };

  const handleClearDatabase = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/clear-database');
      toast.success(`Database berhasil dikosongkan! ${response.data.total_records_deleted} records dihapus`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mengosongkan database'));
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  const handleClearSales = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/clear-sales');
      const data = response.data;
      toast.success(`Tabel Penjualan berhasil dikosongkan! Penjualan: ${data.sales_deleted}, Piutang: ${data.customer_accounts_deleted}, Period Transfers: ${data.period_transfers_deleted}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mengosongkan tabel penjualan'));
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  const handleClearExpenses = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/clear-expenses');
      const data = response.data;
      toast.success(`Tabel Biaya berhasil dikosongkan! Biaya Cabang: ${data.expenses_deleted}, Biaya Pusat: ${data.central_expenses_deleted}, Period Transfers: ${data.period_transfers_deleted}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mengosongkan tabel biaya'));
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  const handleClearPurchases = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/clear-purchases');
      const data = response.data;
      toast.success(`Tabel Pembelian berhasil dikosongkan! Pembelian: ${data.purchases_deleted}, Period Transfers: ${data.period_transfers_deleted}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mengosongkan tabel pembelian'));
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  const handleResetAccountBalances = async () => {
    setLoading(true);
    try {
      const response = await api.post('/admin/reset-account-balances');
      const data = response.data;
      toast.success(`Saldo rekening berhasil direset! Transaksi: ${data.account_transactions_deleted}, Investasi: ${data.investments_deleted}, Rekening: ${data.accounts_reset}, Investor: ${data.investors_reset}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mereset saldo rekening'));
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  // Backup to system
  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await api.post('/admin/backup');
      const data = response.data;
      toast.success(`Backup berhasil! ${data.total_records} records tersimpan ke ${data.filename}`);
      fetchBackups(); // Refresh backup list
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal membuat backup'));
    } finally {
      setBackupLoading(false);
    }
  };

  // Download backup file
  const handleDownloadBackup = async (filename) => {
    try {
      // Ask backend for a short-lived presigned S3 URL, then let the browser
      // download it directly. This shows the native browser download progress
      // and avoids loading large files into memory as a blob.
      const response = await api.get(`/admin/backup/${filename}/download-url`);
      const url = response.data?.url;
      if (!url) throw new Error('URL unduhan tidak tersedia');

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Unduhan ${filename} dimulai`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal mengunduh backup'));
    }
  };

  // Delete backup file
  const handleDeleteBackup = async (filename) => {
    try {
      await api.delete(`/admin/backup/${filename}`);
      toast.success(`Backup ${filename} berhasil dihapus`);
      fetchBackups();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal menghapus backup'));
    } finally {
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    }
  };

  // Restore from system backup
  const handleRestoreFromSystem = async (filename) => {
    setRestoreLoading(true);
    try {
      const response = await api.post('/admin/restore/system', { filename });
      const data = response.data;
      toast.success(`Restore berhasil! ${data.total_restored} records dipulihkan`);
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Gagal melakukan restore'));
    } finally {
      setRestoreLoading(false);
    }
  };

  // File select for upload
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('File harus berformat JSON');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Restore from uploaded file
  const handleRestoreFromUpload = async () => {
    if (!selectedFile) {
      toast.error('Pilih file backup terlebih dahulu');
      return;
    }

    setRestoreLoading(true);
    try {
      const fileContent = await selectedFile.text();
      const backupData = JSON.parse(fileContent);
      
      if (!backupData.collections || !backupData.backup_info) {
        throw new Error('Format backup tidak valid');
      }

      const response = await api.post('/admin/restore/upload', { backup_data: backupData });
      const data = response.data;
      const totalRestored = data.total_restored || data.total_records_restored || 0;
      toast.success(`Restore berhasil! ${totalRestored} records dipulihkan`);
      
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setConfirmDialog({ open: false, action: null, title: '', description: '' });
    } catch (error) {
      if (error.message === 'Format backup tidak valid') {
        toast.error('Format file backup tidak valid');
      } else {
        toast.error(extractErrorMessage(error, 'Gagal melakukan restore'));
      }
    } finally {
      setRestoreLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === undefined || bytes === null || isNaN(bytes)) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!isMaster(user)) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
          <p className="text-slate-600">Hanya Admin Pusat yang dapat mengakses halaman ini.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Database Management</h1>
        <p className="text-slate-600 mt-2">Kelola backup dan data database</p>
      </div>

      {/* Backup Section */}
      <Card className="border-2 border-green-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <HardDrive className="text-green-600" size={24} />
              </div>
              <div>
                <CardTitle className="text-green-900">Backup & Restore</CardTitle>
                <CardDescription>Kelola backup database sistem</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchBackups}
                disabled={backupsLoading}
              >
                <RefreshCw size={16} className={backupsLoading ? 'animate-spin' : ''} />
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleBackup}
                disabled={backupLoading}
              >
                {backupLoading ? (
                  <>Membuat Backup...</>
                ) : (
                  <>
                    <HardDrive size={16} className="mr-2" />
                    Buat Backup Baru
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Backup List */}
          {backups.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>File Backup</TableHead>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Dibuat Oleh</TableHead>
                    <TableHead className="text-center">Sumber</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead className="text-right">Ukuran</TableHead>
                    <TableHead className="text-center">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.filename}>
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-2">
                          <FileJson size={16} className="text-green-600" />
                          {backup.filename}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock size={14} className="text-slate-400" />
                          {formatDate(backup.created_at)}
                        </div>
                      </TableCell>
                      <TableCell>{backup.created_by || '-'}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${backup.source === 's3' || backup.in_s3 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {backup.source === 's3' || backup.in_s3 ? '☁️ S3' : '💾 Lokal'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{backup.total_records?.toLocaleString() || '-'}</TableCell>
                      <TableCell className="text-right">{formatFileSize(backup.size || backup.file_size)}</TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadBackup(backup.filename)}
                            title="Download"
                          >
                            <Download size={14} />
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => showConfirmDialog(
                              () => handleRestoreFromSystem(backup.filename),
                              'Restore Database?',
                              `Restore dari backup "${backup.filename}"? Semua data saat ini akan DIGANTI dengan data dari backup tanggal ${formatDate(backup.created_at)}.`
                            )}
                            title="Restore"
                          >
                            <Upload size={14} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => showConfirmDialog(
                              () => handleDeleteBackup(backup.filename),
                              'Hapus Backup?',
                              `Hapus file backup "${backup.filename}"? File tidak dapat dipulihkan.`
                            )}
                            title="Hapus"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <HardDrive size={48} className="mx-auto mb-3 opacity-30" />
              <p>Belum ada backup tersimpan</p>
              <p className="text-sm">Klik "Buat Backup Baru" untuk membuat backup pertama</p>
            </div>
          )}

          {/* Upload Section */}
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-slate-700 mb-3">Restore dari File</h4>
            <div className="flex gap-3 items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
                id="backup-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={16} className="mr-2" />
                Pilih File JSON
              </Button>
              
              {selectedFile && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-lg">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="text-sm text-slate-700">{selectedFile.name}</span>
                </div>
              )}
              
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => showConfirmDialog(
                  handleRestoreFromUpload,
                  'Restore dari File?',
                  `Restore dari file "${selectedFile?.name}"? Semua data saat ini akan DIGANTI dengan data dari file backup.`
                )}
                disabled={restoreLoading || !selectedFile}
              >
                {restoreLoading ? 'Memulihkan...' : 'Restore'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning Banner */}
      <Card className="border-l-4 border-l-red-500 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 mt-0.5" size={24} />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Peringatan - Operasi Penghapusan</h3>
              <p className="text-sm text-red-800">
                Semua aksi penghapusan di bawah ini <strong>tidak dapat dibatalkan</strong>. 
                Pastikan Anda memiliki backup sebelum melakukan penghapusan data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Clear Sales */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingCart className="text-blue-600" size={24} />
              </div>
              <div>
                <CardTitle>Kosongkan Tabel Penjualan</CardTitle>
                <CardDescription>Hapus semua data penjualan</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Menghapus semua record dari tabel penjualan, piutang pelanggan, dan data Pindah Periode Penjualan.
            </p>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => showConfirmDialog(
                handleClearSales,
                'Kosongkan Tabel Penjualan?',
                'Semua data penjualan, piutang pelanggan, dan Pindah Periode Penjualan akan dihapus secara permanen.'
              )}
              disabled={loading}
            >
              <Trash2 size={16} className="mr-2" />
              Kosongkan Penjualan
            </Button>
          </CardContent>
        </Card>

        {/* Clear Expenses */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Receipt className="text-orange-600" size={24} />
              </div>
              <div>
                <CardTitle>Kosongkan Tabel Biaya</CardTitle>
                <CardDescription>Hapus semua data biaya</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Menghapus semua record dari tabel biaya cabang, biaya pusat, dan data Pindah Periode Biaya.
            </p>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => showConfirmDialog(
                handleClearExpenses,
                'Kosongkan Tabel Biaya?',
                'Semua data biaya cabang, biaya pusat, dan Pindah Periode Biaya akan dihapus secara permanen.'
              )}
              disabled={loading}
            >
              <Trash2 size={16} className="mr-2" />
              Kosongkan Biaya
            </Button>
          </CardContent>
        </Card>

        {/* Clear Purchases */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="text-purple-600" size={24} />
              </div>
              <div>
                <CardTitle>Kosongkan Tabel Pembelian</CardTitle>
                <CardDescription>Hapus semua data pembelian</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Menghapus semua record dari tabel pembelian dan data Pindah Periode Pembelian.
            </p>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => showConfirmDialog(
                handleClearPurchases,
                'Kosongkan Tabel Pembelian?',
                'Semua data pembelian dan Pindah Periode Pembelian akan dihapus secara permanen.'
              )}
              disabled={loading}
            >
              <Trash2 size={16} className="mr-2" />
              Kosongkan Pembelian
            </Button>
          </CardContent>
        </Card>

        {/* Reset Account Balances */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <RotateCcw className="text-teal-600" size={24} />
              </div>
              <div>
                <CardTitle>Reset Saldo Rekening</CardTitle>
                <CardDescription>Reset semua saldo ke 0</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Menghapus history transaksi, dana investasi, dan mengatur semua saldo rekening serta saldo investor menjadi 0.
            </p>
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => showConfirmDialog(
                handleResetAccountBalances,
                'Reset Semua Saldo Rekening?',
                'Semua history transaksi akan dihapus dan saldo rekening akan direset ke 0.'
              )}
              disabled={loading}
            >
              <RotateCcw size={16} className="mr-2" />
              Reset Saldo Rekening
            </Button>
          </CardContent>
        </Card>

        {/* Clear All Database */}
        <Card className="md:col-span-2 border-2 border-red-300">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Database className="text-red-600" size={24} />
              </div>
              <div>
                <CardTitle className="text-red-900">Kosongkan Seluruh Database</CardTitle>
                <CardDescription className="text-red-700">Hapus semua data kecuali users</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Menghapus <strong>SEMUA DATA</strong> dari database termasuk: cabang, mitra, investor, pembelian, biaya, penjualan, periode, 
              transfer, rekening, transaksi, dan activity logs. <strong>Data users tetap dipertahankan.</strong>
            </p>
            <Button
              variant="destructive"
              className="w-full bg-red-600 hover:bg-red-700"
              onClick={() => showConfirmDialog(
                handleClearDatabase,
                'Kosongkan Seluruh Database?',
                'PERINGATAN: Semua data kecuali users akan dihapus secara permanen. Ini akan menghapus semua cabang, mitra, investor, transaksi, dan rekening. Aksi ini TIDAK DAPAT DIBATALKAN!'
              )}
              disabled={loading}
            >
              <Database size={16} className="mr-2" />
              Kosongkan Seluruh Database
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="text-red-600" size={24} />
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-700">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading || restoreLoading}>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.action}
              disabled={loading || restoreLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {(loading || restoreLoading) ? 'Memproses...' : 'Ya, Lanjutkan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DatabaseManagement;
